const PictViewClass = require('pict-view');

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

class ObservationIncluded extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		// set the default data for the included observations
		this.IncludedObservationsGroupedByType = {};
	}

	/**
	 * Build (or rebuild) the included observations list
	 * @param {Boolean} rerender - Whether or not to re-render the panel after building the list
	 * @return {void}
	 */
	buildIncludedObservationsList(rerender = false)
	{
		// group the current observations by type, then re-render the panel
		const tmpObservations = this.AppData?.DocumentData?.ReportData?.Observations;
		this.IncludedObservationsGroupedByType = this.pict.providers.ObservationProvider.groupObservationsByType(tmpObservations);
		if (rerender)
		{
			this.pict.views.HeadlightPanels.renderAsync('HeadlightPanels-ObservationPanel-Included', null, null, null, () =>
			{
				this.log.info('Included observations list rendered.');
			});
		}
	}

	/**
	 * This function is used to handle the removal of an observation from a document.
	 * @param {Event} pEvent - The event for removing an observation from a document.
	 * @param {string} pIDObservation - The observation id to remove from the document
	 * @param {string} pGroupKey - The group key to re-open the collapsible panel
	 * @return {Promise<void>}
	 * @async
	 */
	async handleRemoveIncludedObservation(pEvent, pIDObservation, pGroupKey)
	{
		// prevent onclick event attached to table row that opens detail panel when clicking remove observation
		if (pEvent)
		{
			pEvent.preventDefault();
			pEvent.stopPropagation();
		}
		if (this.AppData.DocumentData.ReportData.Locked)
		{
			this.pict.views.HeadlightPanels.toast({
				message: 'The document is locked and cannot be modified.',
				type: 'is-danger',
			});
			return;
		}
		const tmpObservations = this.AppData?.DocumentData?.ReportData?.Observations;
		if (tmpObservations && Array.isArray(tmpObservations))
		{
			this.AppData.DocumentData.ReportData.Observations = tmpObservations?.filter((observation) => observation.IDObservation != pIDObservation);
		}
		await this.pict.providers.DocumentProvider.handleSave(true);
		// rebuild the included observations list
		this.buildIncludedObservationsList(true);
		// re-open the collapsible panel group if still has items
		if (pGroupKey)
		{
			this.pict.views.HeadlightPanels.toggleCollapsiblePanelBlockGroup(pGroupKey);
		}
		// if the ObservationCardDeckProvider is available, refresh any card decks to reflect the removed observation
		if (this.pict.providers.ObservationCardDeckProvider)
		{
			await this.pict.providers.ObservationCardDeckProvider.refreshCardDecks(pIDObservation, 'remove', this.AppData.DocumentData.ReportData.Observations, 'Panels-Observations-Included');
		}
	}
}

module.exports = ObservationIncluded;

module.exports.default_configuration =
{
	ViewIdentifier: 'ObservationIncluded',
	DefaultRenderable: 'ObservationIncluded',
	DefaultDestinationAddress: '#ObservationIncluded-Container',
	Templates:
		[
			{
				Hash: 'HeadlightPanels-ObservationPanel-Included',
				Template: /*html*/`
				<!-- Observation Panel Content > Included View > Empty Message or Observation List (depending on list length) -->
				{~TIfAbs:HeadlightPanels-ObservationPanel-Included-EmptyMessage:AppData:AppData.DocumentData.ReportData.Observations.length^==^0~}
				{~TIfAbs:HeadlightPanels-ObservationPanel-Included-List:AppData:AppData.DocumentData.ReportData.Observations.length^>^0~}`,
			},
			{
				Hash: 'HeadlightPanels-ObservationPanel-Included-EmptyMessage',
				Template: /*html*/`
				<div id="HeadlightPanels-ObservationPanel-Included-Empty-Message" class="notification has-text-text-light is-dark m-3">
					<p class="has-text-centered">No observations are included in this document.</p>
				</div>`,
			},
			{
				Hash: 'HeadlightPanels-ObservationPanel-Included-List',
				Template: /*html*/`
				<!-- Observation Panel Content > Included View > List -->
				<div id="HeadlightPanels-ObservationPanel-Included-List" class="nested-scroll-content panel-views-height-offset has-preivew-images">
					{~TS:HeadlightPanels-ObservationPanel-Included-List-Group:Pict.views.ObservationIncluded.IncludedObservationsGroupedByType~}
				</div>`,
			},
			{
				Hash: 'HeadlightPanels-ObservationPanel-Included-List-Group',
				Template: /*html*/`
				<div class="panel-block is-collapsible-header pr-6 is-relative" data-collapsible-toggle="{~D:Record.ObservationType~}" onclick="_Pict.views.HeadlightPanels.toggleCollapsiblePanelBlockGroup('{~D:Record.ObservationType~}')">
					<span class="panel-icon panel-block-toggle-icon">
						<i class="fa fa-fw fa-chevron-right"></i>
					</span>
					<span class="panel-icon panel-block-type-icon">
						<i class="fa fa-fw {~D:Record.ObservationTypeDetails.Icon~}"></i>
					</span>
					<span class="has-text-weight-semibold">{~D:Record.ObservationTypeDetails.Type~}</span>
					<span class="tag is-dark has-background-text-15 is-rounded" style="position: absolute; right: 1rem;">{~D:Record.ObservationList.length~}</span>
				</div>
				<div class="is-collapsible-content is-hidden" data-collapsible-content="{~D:Record.ObservationType~}">
					{~TS:HeadlightPanels-ObservationPanel-Included-List-Group-Item:Record.ObservationList~}
				</div>`,
			},
			{
				Hash: 'HeadlightPanels-ObservationPanel-Included-List-Group-Item',
				Template: /*html*/`
				<div id="ObservationPanel-Row" class="panel-block is-block-flex is-align-items-left is-relative p-2 is-clickable" 
					onclick="_Pict.views.HeadlightPaneObservations.handleObservationImagePreviewClick(event, '{~D:Record.IDObservation~}', 'Included')" data-observation-id="{~D:Record.IDObservation~}">
					<a class="preview-image-wrapper" >
						<img class="preview-image" src="/1.0/Observation/{~D:Record.IDObservation~}/Image/Thumbnail-Dense{~CacheBustPostfix:~}" alt="Observation Preview for #{~D:Record.IDObservation~}" />
					</a>
					<div class="is-block-flex is-align-items-left mx-4" style="flex-grow: 1">
						<div class="is-size-7 has-text-text-75">
							{~TIfAbs:HeadlightPanels-ObservationPanel-Included-List-Group-Item-Locked:Record.Locked:Record.Locked^==^1~}
							{~TIfAbs:HeadlightPanels-ObservationPanel-Included-List-Group-Item-Unlocked:Record.Locked:Record.Locked^==^0~}
							<span>#{~D:Record.IDObservation~}</span>
						</div>
						<div>{~D:Record.Name~}</div>
						<div class="is-size-7 has-text-text-75">{~DateFormat:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~}</div>
						<div class="is-size-7 has-text-text-75"><i class="fa fa-fw fa-user"></i> {~Entity:User^Record.CreatingIDUser^HeadlightPanels-ObservationPanel-Included-List-Group-Item-User^~}</div>
					</div>
					<!-- TODO: convert this to a popover -->
					<div class="buttons mr-4" style="max-width: 100px;">
						<button type="button" data-panel-mode-show-if="add-to-set" title="Add observation to set" data-observation-id="{~D:Record.IDObservation~}" class="button is-ghost-icon has-text-success-75 p-0 mr-2"
							onclick="_Pict.views.HeadlightPaneObservations.handleObservationListSelection(event, '{~D:Record.IDObservation~}', 'add-to-set')">
							<span class="icon">
								<i class="fa fa-fw fa-circle-plus"></i>
							</span>
							<span class="add-to-set-link">Add to Set</span>
						</button>
						<button type="button" title="Remove observation from document" class="button is-ghost-icon has-text-text-85 p-0 mr-2" onclick="_Pict.views.ObservationIncluded.handleRemoveIncludedObservation(event, '{~D:Record.IDObservation~}', '{~D:Record.ObservationType~}')">
							<span class="icon">
								<i class="fa fa-fw fa-times-circle"></i>
							</span>
							<span data-panel-mode-show-if="add-to-set" style="white-space: normal;">Remove from Document</span>
						</button>
					</div>
				</div>`,
			},
			{
				Hash: 'HeadlightPanels-ObservationPanel-Included-List-Group-Item-User',
				Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
			},
			{
				Hash: 'HeadlightPanels-ObservationPanel-BannerTarget',
				Template: /*html*/`{~D:Pict.views.HeadlightPaneObservations.AddImageTargetDisplayName~}`,
			},
			{
				Hash: 'HeadlightPanels-ObservationPanel-Included-List-Group-Item-Locked',
				Template: /*html*/`
					<span class=" has-text-text-75" title="Locked">
						<i class="fas fa-fw fa-sm fa-lock"></i>
					</span>`,
			},
			{
				Hash: 'HeadlightPanels-ObservationPanel-Included-List-Group-Item-Unlocked',
				Template: /*html*/`
					<span class="has-text-text-40" title="Unlocked">
						<i class="fas fa-fw fa-sm fa-unlock"></i>
					</span>`,
			},
		],
	Renderables:
		[

		],
};
